import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { type AIContext, useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import type { RouteSidebarEffectParams } from './types';

type Params = {
  pathname: string;
  setAiContextMenu: (context: AIContext | undefined) => void;
} & RouteSidebarEffectParams;

/**
 * ルート変更時にサイドバーの状態を管理する
 *
 * @remarks
 * - 通常のルート変更時: サイドバーを閉じる（ダーティ状態の場合は確認ダイアログ表示）
 * - 特定条件（fromPdf=true, threadId指定）: サイドバーを開く
 *
 * 事業詳細ページの「デフォルトオープン」（ITA-2626）はサイドパネルが閉じた状態でも動作する必要があるため、
 * 常時マウントされる `MainRouteLayout` 側の useEffect で処理する（本フックではなく）。
 */
export const useRouteSidebarEffect = ({ pathname, setAiContextMenu, route, params }: Params) => {
  // `/projects/<id>` と `/projects/<id>/memo` は同じ「事業詳細」コンテキストとして扱い、
  // 両者間の遷移ではドロワーをリセットしない（ITA-2561）。
  // 末尾の `/memo` を取り除いた正規化パスで比較する。
  const normalizedPathname = pathname.replace(/\/memo\/?$/, '');
  const previousPathRef = useRef(normalizedPathname);
  const setRightSidebarOpen = useRouteSidePanelStore((state) => state.setOpen);
  const requestAction = useRouteSidePanelStore((state) => state.requestAction);
  const navigate = useNavigate();

  // 各ルートのパラメータを型安全に抽出
  const fromPdf = route === 'projectCreate' ? params?.fromPdf : undefined;
  const detailThreadId = route === 'projectDetail' ? params?.threadId : undefined;

  useEffect(() => {
    const resetContext = () => {
      if (previousPathRef.current !== normalizedPathname) {
        previousPathRef.current = normalizedPathname;
        requestAction(() => {
          setAiContextMenu(undefined);
          setRightSidebarOpen({ isOpen: false, mode: undefined });
        });
      }
    };

    switch (route) {
      case 'projectCreate': {
        resetContext();
        if (fromPdf) {
          navigate({ to: '/projects/create', search: { fromPdf: false }, replace: true });
          const context: AIContext = { route: 'projectCreate', mode: 'createFromPdf' };
          setAiContextMenu(context);
          setRightSidebarOpen({ isOpen: true, mode: { type: 'ai-chat', context } });
        }
        break;
      }
      case 'projectDetail': {
        const hasProjectChanged = previousPathRef.current !== normalizedPathname;

        // ?threadId=xxx 付きで遷移した場合（メモのスレッドリンク経由など）、
        // 該当スレッドを開いた状態の AI チャットパネルを自動表示する。
        if (detailThreadId) {
          previousPathRef.current = normalizedPathname;
          // 既に同じ threadId で開いている場合は no-op（再 set による subscriber 通知を避ける）
          const current = useRouteSidePanelStore.getState();
          const alreadyOpenWithThread =
            current.isOpen &&
            current.mode?.type === 'ai-chat' &&
            current.mode.context?.route === 'projectDetail' &&
            current.mode.context.mode === 'agent_chat' &&
            current.mode.context.threadId === detailThreadId;
          if (!alreadyOpenWithThread) {
            const context: AIContext = {
              route: 'projectDetail',
              mode: 'agent_chat',
              threadId: detailThreadId,
            };
            setRightSidebarOpen({ isOpen: true, mode: { type: 'ai-chat', context } });
          }
          break;
        }

        // 別事業へ直接遷移した場合（normalizedPathname が変わった = projectId が変わった）、
        // 開いている AI チャットの threadId は前事業のものなので、新規 fresh context にリセットする。
        // 事業詳細・メモ間の遷移では normalizedPathname が同じためこの分岐に入らず、context を維持する。
        if (hasProjectChanged) {
          previousPathRef.current = normalizedPathname;
          const current = useRouteSidePanelStore.getState();
          if (
            current.isOpen &&
            current.mode?.type === 'ai-chat' &&
            current.mode.context?.route === 'projectDetail' &&
            current.mode.context.threadId !== undefined
          ) {
            const context: AIContext = { route: 'projectDetail', mode: 'agent_chat' };
            setRightSidebarOpen({ isOpen: true, mode: { type: 'ai-chat', context } });
          }
        }
        break;
      }
      case 'projectEdit': {
        resetContext();
        break;
      }
      default:
        resetContext();
    }
  }, [
    normalizedPathname,
    navigate,
    setAiContextMenu,
    setRightSidebarOpen,
    requestAction,
    route,
    fromPdf,
    detailThreadId,
  ]);
};
